import { useContext, useEffect, useMemo, useState } from 'react';
import { debounce, differenceWith, isEqual, uniqBy } from 'lodash-es';
import axios from '@lambda/commons/apis/axios';
import dynamic from 'next/dynamic';
import Loading from '@/components/Loading/Loading';
import useLoading from '@/lib/use-loading';
import { QsContext } from '@/lib/use-qs';
import i18n from '@/settings/i18n';
import { SearchResultsOnlyDeals, TopDealProduct } from './types';
import useWindowSize from '@/lib/use-window-size';
import BundleCard from '@/components/collections-v2/common/Collections/BundleCard';

const t = i18n.init();

const DealProductCard = dynamic(() => import('./DealProductCard'), {
  ssr: false,
});

interface Props {
  deals: TopDealProduct[];
  hasNextPage: boolean;
  spotlightDeals: TopDealProduct[] | undefined;
}

export default function InfiniteScrollSearchResults(props: Props) {
  const [qs] = useContext(QsContext);
  const [deals, setDeals] = useState(props.deals);
  const [hasNextPage, setHasNextPage] = useState(props.hasNextPage);
  const [nextPage, setNextPage] = useState(2);

  const [loading, loadMore] = useLoading(async (page: number) => {
    const urlQs = new URLSearchParams({
      ...qs,
      page: page.toString(),
    });

    const url = `/api/topdeals/search?${urlQs}`;

    const response = await axios.get<SearchResultsOnlyDeals>(url);

    setDeals((o) => [...o, ...response.data.deals]);
    setNextPage(page + 1);
    setHasNextPage(response.data.hasNextPage);
  });
  const windowWidth = useWindowSize()[0];

  const bundleCardPosition = useMemo(() => {
    if (typeof window === 'undefined' || !deals.length) return 11;
    const lastDealIndex = deals.length - 1;

    if (windowWidth >= 1280) return Math.min(11, lastDealIndex);
    if (windowWidth >= 640) return Math.min(8, lastDealIndex);

    return Math.min(5, lastDealIndex);
  }, [deals, windowWidth]);

  useEffect(() => setDeals(props.deals), [props.deals]);
  useEffect(() => setHasNextPage(props.hasNextPage), [props.hasNextPage]);

  useEffect(() => {
    const handleScroll = debounce(() => {
      if (window === undefined) return;

      const resultsEl = document.querySelector(
        '#e2e-search-results',
      ) as Element;

      if (resultsEl == null) return;

      const elementScrollHeight = resultsEl.scrollHeight;

      if (
        window.pageYOffset + window.innerHeight > elementScrollHeight &&
        hasNextPage &&
        !loading
      )
        loadMore(nextPage);
    }, 50);

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, [nextPage, hasNextPage, loading]);

  const filteredDeals =
    props.spotlightDeals && props.spotlightDeals?.length >= 12
      ? differenceWith(deals, props.spotlightDeals, isEqual)
      : deals;

  const renderedDeals = uniqBy(filteredDeals, 'productId').map((deal, i) => {
    if (i === bundleCardPosition) {
      return (
        <>
          <BundleCard key={`bundle-card-key-${i}`} />
          <DealProductCard deal={deal} index={i} key={deal.productId} />
        </>
      );
    }

    return <DealProductCard deal={deal} index={i} key={deal.productId} />;
  });

  return (
    <>
      {!deals.length && (
        <>
          <h3 className="mt-3 flex items-center justify-center text-xl font-bold text-black lg:hidden">
            {t`Search results`}
          </h3>
          <p className="pt-2 text-center text-sm text-black sm:text-base lg:pt-4 lg:text-left">
            {t`Sorry, no products matched your selection.`}
          </p>
        </>
      )}
      <div
        className="mt-4 grid gap-3 3xs:grid-cols-2 sm:grid-cols-3 lg:mt-0 xl:!grid-cols-4"
        id="e2e-search-results"
      >
        {renderedDeals}
      </div>

      {loading && (
        <div className="pt-6 text-center">
          <Loading />
        </div>
      )}
    </>
  );
}
